import './compact.css';
import type { ViewerShell } from '../../shell';
import { currentLeftTool, isAnnotationTool, type ToolId } from '../../tools';
import { contextMenuItems, showMenu } from '../contextmenu';
import { COMPACT_MEDIA_QUERY, LONG_PRESS_MS, isDoubleTap, isLongPress } from './mode';

/**
 * The compact chrome of the full workspace: on a small or short window (a phone, a tablet in portrait)
 * the toolbar is one scrolling row, the drawers slide over the image instead of pushing it aside, and
 * touch gets what the mouse had: a long press opens the viewport context menu, a double tap toggles a
 * cell's full screen. Same tools, same bindings; `html[data-chrome]` drives the CSS. Decided by the
 * window (`matchMedia`), never by the user agent. `prepare` runs at boot (no desktop flash while the
 * study loads); `install` runs before the toolbar so the "Estudios" toggle reads the drawer state set here.
 */

type Point = { x: number; y: number; at: number };

const COARSE_POINTER_QUERY = '(pointer: coarse)';
/** Tools a held finger belongs to: Lupa shows the lens while pressed; the measurements place a handle. */
const LONG_PRESS_BLOCKED_TOOLS = new Set<ToolId>(['magnify']);
/** Surfaces inside a cell with their own press handling (the assistant's region picker, the MPR panes). */
const LONG_PRESS_BLOCKED_SURFACES = '.assistant-picker, .mpr-view';

export function isCompactChrome(): boolean {
  return document.documentElement.dataset.chrome === 'compact';
}

/** Mover and Magnificar belong in the strip wherever a finger has no middle or right button. */
export function stripShowsPanZoom(): boolean {
  return isCompactChrome() || window.matchMedia(COARSE_POINTER_QUERY).matches;
}

function applyMode(shell: ViewerShell, compact: boolean, initial: boolean): void {
  document.documentElement.dataset.chrome = compact ? 'compact' : 'regular';
  if (compact) {
    shell.setLeftDrawer(false);
    if (!initial) shell.setRightDrawer(false);
  } else if (!initial) {
    shell.setLeftDrawer(true);
  }
}

/** Sets the chrome mode before anything loads (boot): the CSS lays the loading screen out right away. */
export function prepare(shell: ViewerShell): void {
  applyMode(shell, window.matchMedia(COMPACT_MEDIA_QUERY).matches, true);
}

function trackMode(shell: ViewerShell): void {
  const query = window.matchMedia(COMPACT_MEDIA_QUERY);
  applyMode(shell, query.matches, true);
  query.addEventListener('change', (event) => applyMode(shell, event.matches, false));
}

/**
 * In compact mode the drawers are overlays: one at a time, a series pick closes the studies drawer,
 * a tap on the scrim closes whichever is open.
 */
function installOverlayDrawers(shell: ViewerShell): void {
  shell.on('drawer-changed', ({ side, open }) => {
    if (!isCompactChrome() || !open) return;
    if (side === 'left' && shell.isRightDrawerOpen()) shell.setRightDrawer(false);
    if (side === 'right' && shell.isLeftDrawerOpen()) shell.setLeftDrawer(false);
  });
  shell.on('series-shown', () => {
    if (isCompactChrome() && shell.isLeftDrawerOpen()) shell.setLeftDrawer(false);
  });
  const body = shell.regions.grid.parentElement?.parentElement;
  if (!body) return;
  // The scrim is the body's ::after: a tap on it targets the body itself, never a drawer or the grid.
  body.addEventListener('click', (event) => {
    if (!isCompactChrome() || event.target !== body) return;
    shell.setLeftDrawer(false);
    shell.setRightDrawer(false);
  });
}

function cellIndexAt(shell: ViewerShell, target: EventTarget | null): number {
  const cell = (target as HTMLElement | null)?.closest?.('.viewport-cell') ?? null;
  return cell ? shell.viewports().findIndex((vp) => vp.container === cell) : -1;
}

function longPressAllowed(target: EventTarget | null): boolean {
  const tool = currentLeftTool();
  if (isAnnotationTool(tool) || LONG_PRESS_BLOCKED_TOOLS.has(tool)) return false;
  return !(target as HTMLElement | null)?.closest?.(LONG_PRESS_BLOCKED_SURFACES);
}

/**
 * A finger held still on a cell opens the context menu. cornerstone-tools runs its own press timer
 * (`cornerstonetoolstouchpress`, used by the text tools to edit a label); those are annotation tools,
 * which `longPressAllowed` keeps out, so the two never fire together. Keep that exclusion.
 */
function installLongPress(shell: ViewerShell): void {
  const grid = shell.regions.grid;
  let press: Point | null = null;
  let travel = 0;
  let timer = 0;
  const cancel = () => {
    window.clearTimeout(timer);
    press = null;
  };
  grid.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch' || !event.isPrimary || !longPressAllowed(event.target)) return;
    press = { x: event.clientX, y: event.clientY, at: performance.now() };
    travel = 0;
    timer = window.setTimeout(() => {
      if (press && isLongPress(performance.now() - press.at, travel)) {
        showMenu(shell.regions.overlayLayer, contextMenuItems(shell), press.x, press.y, { className: 'context-menu', idPrefix: 'cm-' });
      }
      press = null;
    }, LONG_PRESS_MS);
  }, { capture: true, passive: true });
  grid.addEventListener('pointermove', (event) => {
    if (press) travel = Math.max(travel, Math.hypot(event.clientX - press.x, event.clientY - press.y));
  }, { capture: true, passive: true });
  grid.addEventListener('pointerup', cancel, { capture: true, passive: true });
  grid.addEventListener('pointercancel', cancel, { capture: true, passive: true });
}

/**
 * Two quick taps on a cell toggle its full screen. cornerstone-tools prevents the native touch events,
 * so the browser synthesises no click or dblclick on a cell: this is the only double-tap handler.
 */
function installDoubleTap(shell: ViewerShell): void {
  const grid = shell.regions.grid;
  let down: Point | null = null;
  let last: Point | null = null;
  grid.addEventListener('pointerdown', (event) => {
    down = event.pointerType === 'touch' && event.isPrimary ? { x: event.clientX, y: event.clientY, at: performance.now() } : null;
  }, { capture: true, passive: true });
  grid.addEventListener('pointerup', (event) => {
    if (!down || event.pointerType !== 'touch' || !event.isPrimary) return;
    const tap: Point = { x: event.clientX, y: event.clientY, at: performance.now() };
    // A drag (stack scroll, pan) is not a tap, and neither is a long press.
    const isTap = isDoubleTap({ gapMs: tap.at - down.at, distancePx: Math.hypot(tap.x - down.x, tap.y - down.y) });
    down = null;
    const double = isTap && last !== null && isDoubleTap({ gapMs: tap.at - last.at, distancePx: Math.hypot(tap.x - last.x, tap.y - last.y) });
    last = isTap && !double ? tap : null;
    if (!double) return;
    const index = cellIndexAt(shell, event.target);
    if (index >= 0) shell.setFullScreen(shell.fullScreenIndex() === index ? undefined : index);
  }, { capture: true, passive: true });
}

export function install(shell: ViewerShell): void {
  trackMode(shell);
  installOverlayDrawers(shell);
  installLongPress(shell);
  installDoubleTap(shell);
}
